/* eslint-disable @typescript-eslint/indent */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useLocation } from 'react-router-dom';
import { Field, Form, Formik } from 'formik';
import {
  ApplicationType,
  CreateApplicationCommandResult,
} from '@sdir/httpclient/lib/clients/aps/application';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { Loader, RadioOption, SdirModal, styles } from '@sdir/sds';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { ErrorWithTrace } from '@sdir/blueprint.aps/lib/components';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { translateCertificateType } from '../../../helpers/Certificates';
import FormRadioButtonGroup, { RadioOnChange } from '../../Molecules/FormRadioButtonGroup';
import { ApplicationLayout } from '../../Organisms';
import { getApplicationApi, sidApplicationApi, sidCertificateApi } from '../../../httpclient';
import { getStepsForSid } from '../../../helpers/Steps';
import {
  useCurrentPerson,
  useCurrentRegisteredPerson,
  useFeatureToggles,
} from '../../../services/hooks';
import { isValidNorwegianPnr } from '../../../helpers/userHelpers';
import { ToggleDisableSid } from '../../../constants/permissions';
import { routes } from '../../../constants/routes';
import { ApplicationStatus } from '../../../types';

type CertificateDetails = {
  certificateCode?: string | null;
  type?: ApplicationType | null;
};

const NewSidApplicationPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const location = useLocation();
  const [showDuplicateInfo, setShowDuplicateInfo] = useState<boolean>(false);
  const [showHasExistingModal, setShowHasExistingModal] = useState<boolean>(false);
  const [submitErrorText, setSubmitErrorText] = useState<string>();
  const {
    person: { result: contactInfo },
  } = useCurrentPerson();
  const {
    person: { result: registeredPerson },
  } = useCurrentRegisteredPerson();
  const featureToggle = useFeatureToggles();
  const enabledSid = !isToggleOn(
    featureToggle?.result,
    ToggleDisableSid,
    contactInfo?.personalIdentificationNumber || ''
  );

  const { hasValidPnr, nameHasChanged } = useMemo(() => {
    const validPnr = isValidNorwegianPnr(
      contactInfo?.personalIdentificationNumber || registeredPerson?.personNumber || ''
    );
    const nameDiffers =
      contactInfo?.firstName !== registeredPerson?.firstName ||
      contactInfo?.middleName !== registeredPerson?.middleName ||
      contactInfo?.lastName !== registeredPerson?.lastName;
    return {
      hasValidPnr: validPnr,
      nameHasChanged: nameDiffers,
    };
  }, [contactInfo, registeredPerson]);

  const { result: sidResults } = useApi({
    apiFunction: () =>
      sidCertificateApi.v1SeafareridentitydocumentSearchUserGet(1, 1000, undefined, 'me'),
    callApiOnInit: true,
  });

  const { result: applications } = useApi({
    apiFunction: () =>
      getApplicationApi.v2GetGetownapplicationsQueryPost({
        filter: {
          status: undefined,
          freeTextSearchStringOnType: undefined,
          applicationTypes: undefined,
          certificateCode: 'SID',
        },
      }),
    callApiOnInit: true,
  });

  const {
    errorObject: submitError,
    loading: submitLoading,
    callApi: submit,
  } = useApi({
    apiFunction: (p) => sidApplicationApi.v1SeafareridentitydocumentCreatesidapplicationBodyPost(p),
    successCallback: (result: CreateApplicationCommandResult) => {
      setSubmitErrorText(undefined);
      if (result?.uid) {
        const firstStep = getStepsForSid()[0];
        navigate(generatePath(firstStep.route, { applicationUid: result.uid }));
      }
    },
  });

  const hasValidSid = useMemo(() => {
    const validSid = (sidResults?.result || []).find((r) => !!r?.isValid);
    return !!validSid;
  }, [sidResults?.result]);

  const { firstTimeApplication, duplicateApplication } = useMemo(() => {
    const firstTime = applications?.result?.find(
      (a) =>
        a.applicationType === ApplicationType.FirstTime &&
        a.applicationStatus !== ApplicationStatus.Rejected
    );
    const duplicate = applications?.result?.find(
      (a) =>
        a.applicationType === ApplicationType.Duplicate &&
        a.applicationStatus !== ApplicationStatus.Rejected
    );
    return {
      firstTimeApplication: firstTime,
      duplicateApplication: duplicate,
    };
  }, [applications?.result]);

  const { predefinedType, predefinedCertificateCode } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const locationAppType = (searchParams.get('type') as ApplicationType | undefined) || null;
    const predefinedCertificate = searchParams.get('certificate') || null;
    return {
      predefinedType: locationAppType || null,
      predefinedCertificateCode: predefinedCertificate || null,
    };
  }, [location.search]);

  const initialValues: CertificateDetails = {
    certificateCode: predefinedCertificateCode,
    type: predefinedType || ApplicationType.FirstTime,
  };
  const [applicationType, setApplicationType] = useState<ApplicationType>(() => {
    return initialValues.type || ApplicationType.ApplicationTypeNotDefined;
  });

  const radioGroupOptions = useMemo(() => {
    const types = [ApplicationType.FirstTime, ApplicationType.Duplicate];
    return types.map<RadioOption>((t) => ({
      label: translateCertificateType(t, intl),
      value: t,
    }));
  }, [intl]);

  const onGoToExistingApplication = () => {
    if (applicationType === ApplicationType.FirstTime) {
      navigate(generatePath(routes.applicationBase, { applicationUid: firstTimeApplication?.uid }));
    } else if (applicationType === ApplicationType.Duplicate) {
      navigate(generatePath(routes.applicationBase, { applicationUid: duplicateApplication?.uid }));
    }
  };

  const handleApplicationTypeChange = useCallback(
    (e: RadioOnChange) => {
      const type = ApplicationType[e.target.value];
      if (type !== applicationType) setApplicationType(type);
    },
    [applicationType]
  );

  const handleSubmit = () => {
    if (hasValidSid && applicationType === ApplicationType.Duplicate) {
      setShowDuplicateInfo(true);
      return;
    }
    if (
      firstTimeApplication?.uid &&
      applicationType === ApplicationType.FirstTime &&
      !nameHasChanged
    ) {
      setShowHasExistingModal(true);
      return;
    }
    if (
      duplicateApplication?.uid &&
      applicationType === ApplicationType.Duplicate &&
      !nameHasChanged
    ) {
      setShowHasExistingModal(true);
      return;
    }
    setShowDuplicateInfo(false);
    setSubmitErrorText(undefined);
    submit?.({ applicationType });
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        type: Yup.string()
          .required(intl.formatMessage({ id: 'general.form.error' }))
          .nullable(),
      }),
    [intl]
  );

  useEffect(() => {
    if (submitError?.response?.status === 422) {
      setSubmitErrorText(submitError?.response?.data?.message || null);
    }
  }, [submitError]);

  if (!hasValidPnr || !enabledSid) return <></>;

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ submitForm }) => (
          <Form noValidate>
            <ApplicationLayout
              topHeading=""
              heading=""
              exitLabel={intl.formatMessage({ id: 'general.form.back' })}
              mainContentMaxWidth="100%"
              canNavBack={!submitLoading}
              canNavNext={!submitLoading}
              navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
              navNextLabel={intl.formatMessage({ id: 'general.form.next' })}
              onNavNextClick={submitForm}
              onNavBackClick={() => navigate(-1)}
              onExitClick={() => navigate(-1)}
            >
              <Wrapper>
                <InnerWrapper>
                  <H2>{intl.formatMessage({ id: 'sidOverview.button.newapplication' })}</H2>
                  <AppType>
                    <Label htmlFor="appType">
                      {intl.formatMessage({ id: 'certificate.app.type' })}
                    </Label>
                    <RadioButtonDiv>
                      <Field
                        name="type"
                        radioContainer={CustomContainer}
                        options={radioGroupOptions}
                        disabled
                        component={FormRadioButtonGroup}
                        onChange={handleApplicationTypeChange}
                      />
                    </RadioButtonDiv>
                  </AppType>
                </InnerWrapper>
                {submitLoading && <Loader />}
              </Wrapper>
              {showDuplicateInfo && (
                <ErrorWrapper>
                  <ErrorMessage
                    title={intl.formatMessage({ id: 'sidCreate.info.duplicate.title' })}
                    error={intl.formatMessage({ id: 'sidCreate.info.duplicate.text1' })}
                  >
                    <DuplicateInfo>
                      <span>{intl.formatMessage({ id: 'sidCreate.info.duplicate.text2' })}</span>
                      <span>{intl.formatMessage({ id: 'sidCreate.info.duplicate.text3' })}</span>
                      <FormattedMessage
                        tagName="span"
                        id="sidCreate.info.duplicate.text4"
                        values={{
                          b: (chunks) => <b>{chunks}</b>,
                        }}
                      />
                      <span>{intl.formatMessage({ id: 'sidCreate.info.duplicate.text5' })}</span>
                    </DuplicateInfo>
                  </ErrorMessage>
                </ErrorWrapper>
              )}
              {(submitErrorText || submitError) && (
                <ErrorWrapper>
                  <ErrorWithTrace
                    error={submitErrorText || submitError}
                    trace={submitError?.response?.headers?.traceresponse}
                  />
                </ErrorWrapper>
              )}
            </ApplicationLayout>
          </Form>
        )}
      </Formik>
      <SdirModal
        visible={showHasExistingModal}
        onCancel={() => setShowHasExistingModal(false)}
        cancelText={intl.formatMessage({ id: 'general.button.close' })}
        closeButton
        okText={intl.formatMessage({ id: 'certificate.app.exists.button' })}
        onOK={onGoToExistingApplication}
      >
        <ModalHeader>{intl.formatMessage({ id: 'certificate.app.exists.head' })}</ModalHeader>
        {intl.formatMessage({ id: 'certificate.app.exists.text' })}
      </SdirModal>
    </>
  );
};

export default NewSidApplicationPage;
const H2 = styled.h2``;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  background: white;
  padding: 3rem 5rem;
  margin-top: 20px;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    flex-direction: column;
  }
`;

const ModalHeader = styled.h2`
  margin-top: 0;
`;

const InnerWrapper = styled.div`
  min-width: 420px;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;
    min-width: 0;
  }
`;

const ErrorWrapper = styled.div`
  margin-top: 30px;
`;

const RadioButtonDiv = styled.div`
  margin: 1rem 0 1rem 0;
  max-width: 416px;
`;
const AppType = styled.div`
  margin-top: 4rem;
`;
const CustomContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  [data-id='base-item'] {
    padding: 1rem;
  }
`;

const DuplicateInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;

  span {
    &:last-child {
      margin-top: 10px;
    }
  }
`;

const Label = styled.label`
  font-size: 1.6rem;
  color: ${styles.colors.font.textTable};
`;
